import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import type {
    CategorizationRule,
    DataModel,
    FilterGroupKey,
    MetricSection,
    PlanConfig,
    ReportConfig,
    ReportConfigurationLookupOption,
    RuleCondition,
    RuleNode,
    RuleSet,
} from "../types";
import {
    TRIP_DATA_FIELDS,
    breakdownColumnForModel,
    detailColumnsForModel,
    findSelectedDetailColumn,
    findTimestampColumn,
    formatReportDate,
    isCustomReportingPeriod,
    isRuleGroupNode,
    metricFilterLogic,
    metricFilterNodes,
    periodPlainEnglish,
    reportingPeriodName,
    ruleNodeJoin,
    ruleOpDef,
    ruleResultValue,
    ruleResultLabel,
    reportColumnDisplayLabel,
    targetFields,
    PERIOD_DATE_LABEL,
    extraBreakdownLabel,
    periodDateNarrative,
    usesCombinedRuleSets,
    combinedRuleSetIds,
} from "../reportConfigurationLogic";

// Combined categorizations: the rule sets' names, in order ("" when the metric doesn't combine).
function combinedRuleSetNames(report: ReportConfig, section: MetricSection) {
    if (!usesCombinedRuleSets(section)) return "";
    return combinedRuleSetIds(section).map((id) => report.bucketRuleSets.find((set) => set.id === id)?.name || "Missing rule set").join(", ") || "Not selected";
}

type RuleSummaryPreviewMode = "summary" | "details";

interface RuleSummaryPreviewProps {
    plan: PlanConfig;
    report?: ReportConfig;
    mode: RuleSummaryPreviewMode;
    dataModels: Record<string, DataModel>;
    // Every active health plan's client, used only to turn a metric's coPlanClientIds into
    // health plan names. Optional so the preview still renders (ids shown raw) if the
    // client list has not loaded yet.
    clientOptions?: ReportConfigurationLookupOption[];
}

interface FlatCondition {
    id: string;
    field: string;
    operator: string;
    values: string[];
}

export function RuleSummaryPreview({ plan, report, mode, dataModels, clientOptions }: RuleSummaryPreviewProps) {
    if (!report) return <PreviewEmpty title="Select a report" text="Choose a report to see its rule summary." />;
    if (!report.models.length) return <PreviewEmpty title={report.label} text="No data models are attached yet." />;

    return mode === "summary"
        ? <RuleSummaryOverview plan={plan} report={report} dataModels={dataModels} clientOptions={clientOptions} />
        : <RuleSummaryDetails plan={plan} report={report} dataModels={dataModels} clientOptions={clientOptions} />;
}

function RuleSummaryOverview({ plan, report, dataModels, clientOptions }: { plan: PlanConfig; report: ReportConfig; dataModels: Record<string, DataModel>; clientOptions?: ReportConfigurationLookupOption[] }) {
    const categories = useMemo(() => reportCategories(report), [report]);

    return (
        <div className="rc-rule-summary">
            <RuleSummaryHero report={report} compact />
            <div className="rc-rule-summary-overview">
                <SummaryFact icon="health_and_safety" label="Health Plan" value={plan.clientName || plan.name || "Selected Health Plan"} />
                {!!reportCoPlanLabels(report, clientOptions).length && (
                    <SummaryFact icon="groups" label="Co-Plans" value={reportCoPlanLabels(report, clientOptions).join(", ")} />
                )}
                <SummaryFact icon="query_stats" label="Metric" value={metricHeadline(report.sections[0])} />
                <SummaryFact icon="event_repeat" label="Schedule" value={report.frequency || "Not scheduled"} />
            </div>
            <section className="rc-summary-card neutral">
                <SectionTitle icon="category" title="Categories" />
                {categories.length ? (
                    <div className="rc-summary-chip-list">
                        {categories.map((category) => <span className="rc-summary-chip category" key={category}>{category}</span>)}
                    </div>
                ) : <p className="rc-summary-muted">No categorization rules are configured.</p>}
            </section>
            <PlainEnglishSummary plan={plan} report={report} dataModels={dataModels} clientOptions={clientOptions} />
        </div>
    );
}

function RuleSummaryDetails({ plan, report, dataModels, clientOptions }: { plan: PlanConfig; report: ReportConfig; dataModels: Record<string, DataModel>; clientOptions?: ReportConfigurationLookupOption[] }) {
    const [openRuleSetIds, setOpenRuleSetIds] = useState<Set<string>>(new Set());
    const [highlightRuleSetId, setHighlightRuleSetId] = useState("");

    const viewRuleSet = (ruleSetId: string) => {
        setOpenRuleSetIds((current) => new Set(current).add(ruleSetId));
        setHighlightRuleSetId(ruleSetId);
        window.setTimeout(() => {
            document.getElementById(ruleSetDomId(ruleSetId))?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 0);
        window.setTimeout(() => setHighlightRuleSetId(""), 1600);
    };

    return (
        <div className="rc-rule-summary">
            <RuleSummaryHero report={report} />
            <DataScopeCard plan={plan} report={report} clientOptions={clientOptions} />
            <ScheduleSummary report={report} />
            <MetricSummaryCards plan={plan} report={report} dataModels={dataModels} clientOptions={clientOptions} onViewRuleSet={viewRuleSet} />
            <CategorizationRules
                report={report}
                openRuleSetIds={openRuleSetIds}
                highlightedRuleSetId={highlightRuleSetId}
                onToggleRuleSet={(ruleSetId) => setOpenRuleSetIds((current) => {
                    const next = new Set(current);
                    if (next.has(ruleSetId)) next.delete(ruleSetId);
                    else next.add(ruleSetId);
                    return next;
                })}
            />
            <ReportExclusionsCard report={report} />
        </div>
    );
}

function RuleSummaryHero({ report, compact = false }: { report: ReportConfig; compact?: boolean }) {
    const primaryMetric = report.sections[0];
    const ruleSet = primaryMetric?.breakdownSource === "ruleSet"
        ? report.bucketRuleSets.find((set) => set.id === primaryMetric.ruleSetId)
        : null;

    return (
        <section className={compact ? "rc-rule-summary-hero compact" : "rc-rule-summary-hero"}>
            <div>
                <span className="rc-summary-eyebrow">Rule Summary</span>
                <h3>{metricHeadline(primaryMetric)}</h3>
                <p>{ruleSet?.name || report.label}</p>
            </div>
            <span className="rc-summary-schedule-pill">
                <span className="material-icons md-16" aria-hidden="true">event_repeat</span>
                {report.frequency || "Not scheduled"}
            </span>
        </section>
    );
}

function DataScopeCard({ plan, report, clientOptions }: { plan: PlanConfig; report: ReportConfig; clientOptions?: ReportConfigurationLookupOption[] }) {
    // Co-Plans are chosen per metric, not per report, so this row is a roll-up: it names
    // every Co-Plan used anywhere in the report and points at the Metrics section for
    // which metric uses which. Without it, "Health Plan / Applied Automatically" would
    // read as the whole scope even when a metric reaches past this plan.
    const coPlans = reportCoPlanLabels(report, clientOptions);
    return (
        <section className="rc-summary-card scope">
            <SectionTitle icon="dataset" title="Data Scope" />
            <DefinitionGrid
                rows={[
                    ["Health Plan", plan.clientName || plan.name || "Selected Health Plan"],
                    ["Co-Plans", coPlans.length ? `${coPlans.join(", ")} (per metric - see Metrics below)` : "None"],
                    ["Applied", "Automatically"],
                ]}
            />
        </section>
    );
}

function MetricSummaryCards({ plan, report, dataModels, clientOptions, onViewRuleSet }: { plan: PlanConfig; report: ReportConfig; dataModels: Record<string, DataModel>; clientOptions?: ReportConfigurationLookupOption[]; onViewRuleSet: (ruleSetId: string) => void }) {
    const [openMetricIds, setOpenMetricIds] = useState<Set<string>>(new Set());

    if (!report.sections.length) {
        return (
            <section className="rc-summary-card metric">
                <SectionTitle icon="query_stats" title="Metrics" />
                <p className="rc-summary-muted">No metrics are configured yet.</p>
            </section>
        );
    }

    return (
        <section className="rc-summary-card metric">
            <SectionTitle icon="query_stats" title="Metrics" />
            <div className="rc-metric-summary-list">
                {report.sections.map((section, index) => (
                    <MetricSummaryCard
                        plan={plan}
                        report={report}
                        section={section}
                        dataModels={dataModels}
                        clientOptions={clientOptions}
                        index={index}
                        key={section.id}
                        open={openMetricIds.has(section.id)}
                        onToggle={() => setOpenMetricIds((current) => {
                            const next = new Set(current);
                            if (next.has(section.id)) next.delete(section.id);
                            else next.add(section.id);
                            return next;
                        })}
                        onViewRuleSet={onViewRuleSet}
                    />
                ))}
            </div>
        </section>
    );
}

function MetricSummaryCard({
    plan,
    report,
    section,
    dataModels,
    clientOptions,
    index,
    open,
    onToggle,
    onViewRuleSet,
}: {
    plan: PlanConfig;
    report: ReportConfig;
    section: MetricSection;
    dataModels: Record<string, DataModel>;
    clientOptions?: ReportConfigurationLookupOption[];
    index: number;
    open: boolean;
    onToggle: () => void;
    onViewRuleSet: (ruleSetId: string) => void;
}) {
    const [exclusionsOpen, setExclusionsOpen] = useState(false);
    const [inclusionsOpen, setInclusionsOpen] = useState(false);
    const model = section.model ? dataModels[section.model] : null;
    const basedOnColumn = section.model
        ? findTimestampColumn(model || undefined, report.modelBasedOn[section.model])
        : null;
    const ruleSet = metricRuleSet(report, section);
    const breakdown = metricBreakdownLabel(report, section, ruleSet);
    const includeRows = section.include.length && section.model
        ? [conditionFromValues(`${section.id}-include`, breakdown, "Is Any Of", section.include)]
        : [];
    const includeFilterNodes = metricFilterNodes(section, "filter");
    const metricInclusionCount = includeRows.length + countLeafConditions(includeFilterNodes);
    const excludeRows = metricValueExclusions(section, breakdown);
    const excludeFilterNodes = metricFilterNodes(section, "excludeFilter");
    const metricExclusionCount = excludeRows.length + countLeafConditions(excludeFilterNodes);
    const groupBy = groupByLabels(report, section);
    const panelId = `rc-summary-metric-${safeDomPart(section.id)}`;

    return (
        <article className={open ? "rc-metric-summary-card open" : "rc-metric-summary-card"}>
            <button
                type="button"
                className="rc-summary-metric-trigger"
                aria-expanded={open}
                aria-controls={panelId}
                onClick={onToggle}
            >
                <span className="rc-summary-rule-number">{String(index + 1).padStart(2, "0")}</span>
                <span className="rc-summary-rule-title">
                    <strong>{section.name || "Metric"}</strong>
                    <small>{model?.label || "No data model"} / {displayAsLabel(section.displayAs)}</small>
                </span>
                <span className="rc-summary-metric-meta">
                    {groupBy.length ? `${groupBy.length} group${groupBy.length === 1 ? "" : "s"}` : "No grouping"}
                </span>
                <span className="material-icons md-18 rc-summary-chevron" aria-hidden="true">expand_more</span>
            </button>
            {open && (
                <div className="rc-summary-metric-body" id={panelId}>
                    <DefinitionGrid
                        rows={[
                            usesCombinedRuleSets(section)
                                ? ["Rule Sets (combined)", combinedRuleSetNames(report, section)]
                                : ["Rule Set", ruleSet?.name || (section.breakdownSource === "ruleSet" ? "Not selected" : "None")],
                            ["Applied To", model?.label ? `${model.label} Records` : "Records"],
                            // The client scope this metric actually queries: the report plan
                            // plus its Co-Plans, in the same order the generated WHERE clause
                            // lists them.
                            ["Health Plans", clientScopeLabels(plan, section, clientOptions).join(", ")],
                            ["Date Field", section.periodDate?.applyToReportingPeriod ? `${PERIOD_DATE_LABEL} (see below)` : basedOnColumn?.label || "Not selected"],
                            ...(section.periodDate ? [[PERIOD_DATE_LABEL, periodDateNarrative(report, section) || "Not finished"] as [string, string]] : []),
                            ["Period", periodLabel(report)],
                            ["Display", displayAsLabel(section.displayAs)],
                        ]}
                    />
                    {section.breakdownSource === "ruleSet" && (
                        <div className="rc-summary-subblock">
                            <span className="rc-summary-subtitle">Categorization Rule</span>
                            {ruleSet ? (
                                <div className="rc-summary-link-row">
                                    <span>{ruleSet.name}</span>
                                    <button className="rc-summary-link-action" type="button" onClick={() => onViewRuleSet(ruleSet.id)}>
                                        <span className="material-icons md-16" aria-hidden="true">subdirectory_arrow_right</span>
                                        View Rules
                                    </button>
                                </div>
                            ) : <p className="rc-summary-muted">No categorization rule selected.</p>}
                        </div>
                    )}
                    {!!coPlanLabels(section, clientOptions).length && (
                        <div className="rc-summary-subblock">
                            <span className="rc-summary-subtitle">Co-Plans</span>
                            <div className="rc-summary-chip-list">
                                {coPlanLabels(section, clientOptions).map((label) => <span className="rc-summary-chip" key={label}>{label}</span>)}
                            </div>
                        </div>
                    )}
                    <div className="rc-summary-subblock">
                        <span className="rc-summary-subtitle">Group By</span>
                        {groupBy.length ? (
                            <div className="rc-summary-chip-list">
                                {groupBy.map((label) => <span className="rc-summary-chip" key={label}>{label}</span>)}
                            </div>
                        ) : <p className="rc-summary-muted">No grouping configured.</p>}
                    </div>
                    {section.displayAs === "detail" && (
                        <div className="rc-summary-subblock">
                            <span className="rc-summary-subtitle">Detail Columns</span>
                            <div className="rc-summary-chip-list">
                                {detailColumnLabels(section).map((label) => <span className="rc-summary-chip" key={label}>{label}</span>)}
                            </div>
                        </div>
                    )}
                    <div className="rc-summary-subblock">
                        <button
                            type="button"
                            className="rc-summary-subblock-trigger"
                            aria-expanded={inclusionsOpen}
                            onClick={() => setInclusionsOpen(!inclusionsOpen)}
                        >
                            <span>Metric-level Inclusions ({metricInclusionCount})</span>
                            <span className="material-icons md-18" aria-hidden="true">expand_more</span>
                        </button>
                        {inclusionsOpen && (
                            <div className="rc-summary-subblock-body">
                                {metricInclusionCount ? (
                                    <>
                                        {includeRows.map((condition, index) => (
                                            <div className="rc-summary-flat-with-connector" key={condition.id}>
                                                {index > 0 && <Connector op="or" />}
                                                <FlatConditionRow condition={condition} />
                                            </div>
                                        ))}
                                        {!!includeFilterNodes.length && <ConditionGroup report={report} nodes={includeFilterNodes} logic={metricFilterLogic(section, "filter")} />}
                                    </>
                                ) : <p className="rc-summary-muted">No metric-level inclusions configured.</p>}
                            </div>
                        )}
                    </div>
                    <div className="rc-summary-subblock">
                        <button
                            type="button"
                            className="rc-summary-subblock-trigger"
                            aria-expanded={exclusionsOpen}
                            onClick={() => setExclusionsOpen(!exclusionsOpen)}
                        >
                            <span>Metric-level Exclusions ({metricExclusionCount})</span>
                            <span className="material-icons md-18" aria-hidden="true">expand_more</span>
                        </button>
                        {exclusionsOpen && (
                            <div className="rc-summary-subblock-body">
                                {metricExclusionCount ? (
                                    <>
                                        {excludeRows.map((condition, index) => (
                                            <div className="rc-summary-flat-with-connector" key={condition.id}>
                                                {index > 0 && <Connector op="or" />}
                                                <FlatConditionRow condition={condition} />
                                            </div>
                                        ))}
                                        {!!excludeFilterNodes.length && <ConditionGroup report={report} nodes={excludeFilterNodes} logic={metricFilterLogic(section, "excludeFilter")} tone="warning" />}
                                    </>
                                ) : <p className="rc-summary-muted">No metric-level exclusions configured.</p>}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </article>
    );
}

function CategorizationRules({
    report,
    openRuleSetIds,
    highlightedRuleSetId,
    onToggleRuleSet,
}: {
    report: ReportConfig;
    openRuleSetIds: Set<string>;
    highlightedRuleSetId: string;
    onToggleRuleSet: (ruleSetId: string) => void;
}) {
    const [openCategoryKeys, setOpenCategoryKeys] = useState<Set<string>>(new Set());

    if (!report.bucketRuleSets.length) {
        return (
            <section className="rc-summary-card rules">
                <SectionTitle icon="account_tree" title="Categorization Rules" />
                <p className="rc-summary-muted">No rule sets are configured.</p>
            </section>
        );
    }

    const toggleCategory = (key: string) => setOpenCategoryKeys((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
    });

    return (
        <section className="rc-summary-card rules">
            <SectionTitle icon="account_tree" title="Categorization Rules" />
            <div className="rc-summary-rule-list">
                {report.bucketRuleSets.map((ruleSet) => {
                    const open = openRuleSetIds.has(ruleSet.id);
                    const groups = categorizationGroups(ruleSet, report);
                    return (
                        <article
                            className={["rc-summary-ruleset", open ? "open" : "", highlightedRuleSetId === ruleSet.id ? "highlight" : ""].filter(Boolean).join(" ")}
                            id={ruleSetDomId(ruleSet.id)}
                            key={ruleSet.id}
                        >
                            <button
                                type="button"
                                className="rc-summary-rule-trigger ruleset"
                                aria-expanded={open}
                                onClick={() => onToggleRuleSet(ruleSet.id)}
                            >
                                <span className="rc-summary-rule-number"><span className="material-icons md-16" aria-hidden="true">account_tree</span></span>
                                <span className="rc-summary-rule-title">
                                    <strong>{ruleSet.name || "Categorization"}</strong>
                                    <small>{groups.length} categor{groups.length === 1 ? "y" : "ies"} / {ruleSet.rules.length} rule{ruleSet.rules.length === 1 ? "" : "s"}</small>
                                </span>
                                <span className="material-icons md-18 rc-summary-chevron" aria-hidden="true">expand_more</span>
                            </button>
                            {open && (
                                <div className="rc-summary-ruleset-body">
                                    {groups.map((group) => {
                                        const key = `${ruleSet.id}:${group.category}`;
                                        const categoryOpen = openCategoryKeys.has(key);
                                        return (
                                            <CategoryRuleGroup
                                                key={key}
                                                report={report}
                                                ruleSet={ruleSet}
                                                category={group.category}
                                                rules={group.rules}
                                                open={categoryOpen}
                                                onToggle={() => toggleCategory(key)}
                                            />
                                        );
                                    })}
                                    <DefaultCategoryRow ruleSet={ruleSet} />
                                </div>
                            )}
                        </article>
                    );
                })}
            </div>
        </section>
    );
}

function CategoryRuleGroup({
    report,
    ruleSet,
    category,
    rules,
    open,
    onToggle,
}: {
    report: ReportConfig;
    ruleSet: RuleSet;
    category: string;
    rules: CategorizationRule[];
    open: boolean;
    onToggle: () => void;
}) {
    const conditionCount = rules.reduce((sum, rule) => sum + countLeafConditions(rule.conditions), 0);
    const panelId = `rc-rule-summary-${safeDomPart(ruleSet.id)}-${safeDomPart(category)}`;

    return (
        <article className={open ? "rc-summary-category-card open" : "rc-summary-category-card"}>
            <button
                type="button"
                className="rc-summary-rule-trigger category"
                aria-expanded={open}
                aria-controls={panelId}
                onClick={onToggle}
            >
                <span className="rc-summary-rule-number"><span className="material-icons md-16" aria-hidden="true">category</span></span>
                <span className="rc-summary-rule-title">
                    <strong>{category}</strong>
                    <small>{rules.length} rule{rules.length === 1 ? "" : "s"} / {conditionCount} condition{conditionCount === 1 ? "" : "s"}</small>
                </span>
                <ResultBadge label="Result" value={category} />
                <span className="material-icons md-18 rc-summary-chevron" aria-hidden="true">expand_more</span>
            </button>
            {open && (
                <div className="rc-summary-rule-body" id={panelId}>
                    {rules.map((rule, index) => (
                        <CategorizationRuleCard report={report} rule={rule} index={index} key={rule.id} />
                    ))}
                </div>
            )}
        </article>
    );
}

function CategorizationRuleCard({ report, rule, index }: { report: ReportConfig; rule: CategorizationRule; index: number }) {
    const result = ruleResult(rule, report);

    return (
        <div className="rc-summary-category-rule">
            <span className="rc-summary-subtitle">Rule {index + 1}</span>
            <span className="rc-summary-if">IF</span>
            <ConditionGroup report={report} nodes={rule.conditions} logic={rule.conditionLogic} />
            <div className="rc-summary-then">
                <span>THEN</span>
                <ResultBadge label={result.field} value={result.value || result.action} />
            </div>
        </div>
    );
}

function DefaultCategoryRow({ ruleSet }: { ruleSet: RuleSet }) {
    if (ruleSet.includeElse === false) {
        return (
            <div className="rc-summary-default-category">
                <span>
                    <strong>No Else category</strong>
                    <small>Items that do not match any rule will not be shown in the report.</small>
                </span>
                <ResultBadge label="Fallback" value="Not included" muted />
            </div>
        );
    }

    return (
        <div className="rc-summary-default-category">
            <span>
                <strong>{ruleSet.elseLabel.trim() || "Keep Original Value"}</strong>
                <small>Default / Fallback Category. Applied when no categorization rule matches.</small>
            </span>
            <ResultBadge label="Fallback" value={ruleSet.elseLabel.trim() || "Keep Original Value"} muted />
        </div>
    );
}

function ConditionGroup({
    report,
    nodes,
    logic,
    depth = 0,
    tone = "default",
}: {
    report: ReportConfig;
    nodes: RuleNode[];
    logic: "and" | "or";
    depth?: number;
    tone?: "default" | "warning";
}) {
    if (!nodes.length) return <p className="rc-summary-muted">No conditions configured.</p>;

    const runs = andRuns(nodes, logic);
    return (
        <div className={["rc-summary-condition-level", depth ? "nested" : "", tone === "warning" ? "warning" : ""].filter(Boolean).join(" ")}>
            {runs.map((run, runIndex) => (
                <div className="rc-summary-run-wrap" key={`${depth}-${runIndex}`}>
                    {runIndex > 0 && <Connector op="or" />}
                    <div className={run.length > 1 && runs.length > 1 ? "rc-summary-and-run grouped" : "rc-summary-and-run"}>
                        {run.map((node, nodeIndex) => (
                            <div className="rc-summary-node-wrap" key={node.id || `${runIndex}-${nodeIndex}`}>
                                {nodeIndex > 0 && <Connector op="and" />}
                                {isRuleGroupNode(node) ? (
                                    <div className="rc-summary-nested-group">
                                        <div className="rc-summary-nested-title">
                                            <span className="material-icons md-16" aria-hidden="true">schema</span>
                                            {conditionLevelLabel(node.conditions, node.conditionLogic)}
                                        </div>
                                        <ConditionGroup report={report} nodes={node.conditions} logic={node.conditionLogic} depth={depth + 1} tone={tone} />
                                    </div>
                                ) : <ConditionRow report={report} condition={node as RuleCondition} />}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function ConditionRow({ report, condition }: { report: ReportConfig; condition: RuleCondition }) {
    const columnDisplay = reportColumnDisplayLabel(report, condition.column);
    const operator = ruleOpDef(condition.operator);
    const values = conditionValues(condition);
    return (
        <div className="rc-summary-condition-row">
            <span className="rc-summary-token field">{columnDisplay || condition.column || "Field not selected"}</span>
            <span className="rc-summary-token operator">{operator.label}</span>
            {operator.valueMode !== "none" && (
                <span className={values.length > 1 ? "rc-summary-token value multi" : "rc-summary-token value"}>
                    {values.map((value) => <span className="rc-summary-value-chip" key={value}>{value}</span>)}
                </span>
            )}
        </div>
    );
}

function FlatConditionRow({ condition }: { condition: FlatCondition }) {
    return (
        <div className="rc-summary-condition-row flat">
            <span className="rc-summary-token field">{condition.field}</span>
            <span className="rc-summary-token operator">{condition.operator}</span>
            <span className={condition.values.length > 1 ? "rc-summary-token value multi" : "rc-summary-token value"}>
                {condition.values.map((value) => <span className="rc-summary-value-chip" key={value}>{value}</span>)}
            </span>
        </div>
    );
}

function ReportExclusionsCard({ report }: { report: ReportConfig }) {
    const flat = exclusionConditions(report);
    const tripExcludes = tripExclusionGroups(report);

    if (!flat.length && !tripExcludes.length) {
        return (
            <section className="rc-summary-card exclusions">
                <SectionTitle icon="block" title="Report Exclusions" />
                <p className="rc-summary-muted">No report-level exclusions are configured.</p>
            </section>
        );
    }

    return (
        <section className="rc-summary-card exclusions">
            <SectionTitle icon="block" title="Report Exclusions" />
            <p className="rc-summary-muted">These report-level filters apply before metric-specific rules.</p>
            {flat.map((condition, index) => (
                <div className="rc-summary-flat-with-connector" key={condition.id}>
                    {index > 0 && <Connector op="or" />}
                    <FlatConditionRow condition={condition} />
                </div>
            ))}
            {tripExcludes.map((group) => (
                <div className="rc-summary-subblock" key={group.id}>
                    <span className="rc-summary-subtitle">{group.title}</span>
                    {group.conditions.map((condition, index) => (
                        <div className="rc-summary-flat-with-connector" key={condition.id}>
                            {index > 0 && <Connector op={group.logic} />}
                            <FlatConditionRow condition={condition} />
                        </div>
                    ))}
                </div>
            ))}
        </section>
    );
}

function ScheduleSummary({ report }: { report: ReportConfig }) {
    return (
        <section className="rc-summary-card schedule">
            <SectionTitle icon="event_repeat" title="Schedule" />
            <DefinitionGrid rows={[
                ["Runs", report.frequency || "Not scheduled"],
                ["Effective Start", formatReportDate(report.effectiveStart) || "Not set"],
                ["Effective End", formatReportDate(report.effectiveEnd) || "Ongoing"],
            ]} />
        </section>
    );
}

function SectionTitle({ icon, title }: { icon: string; title: string }) {
    return (
        <h5 className="rc-summary-section-title">
            <span className="material-icons md-16" aria-hidden="true">{icon}</span>
            {title}
        </h5>
    );
}

function DefinitionGrid({ rows }: { rows: [string, string][] }) {
    return (
        <dl className="rc-summary-def-grid">
            {rows.map(([label, value]) => (
                <div key={`${label}:${value}`}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                </div>
            ))}
        </dl>
    );
}

function SummaryFact({ icon, label, value }: { icon: string; label: string; value: string }) {
    return (
        <div className="rc-summary-fact">
            <span className="material-icons md-16" aria-hidden="true">{icon}</span>
            <span>
                <small>{label}</small>
                <strong>{value}</strong>
            </span>
        </div>
    );
}

function Connector({ op }: { op: "and" | "or" }) {
    return <span className={`rc-summary-connector ${op}`}>{op.toUpperCase()}</span>;
}

function ResultBadge({ label, value, muted = false }: { label: string; value: string; muted?: boolean }) {
    return (
        <span className={muted ? "rc-summary-result muted" : "rc-summary-result"}>
            <span>{label}</span>
            <span className="material-icons md-16" aria-hidden="true">east</span>
            <b>{value}</b>
        </span>
    );
}

function PlainEnglishSummary({ plan, report, dataModels, clientOptions }: { plan: PlanConfig; report: ReportConfig; dataModels: Record<string, DataModel>; clientOptions?: ReportConfigurationLookupOption[] }) {
    const reportExclusions = reportExclusionDescriptions(report);
    const coPlans = reportCoPlanLabels(report, clientOptions);

    return (
        <section className="rc-summary-card plain">
            <SectionTitle icon="notes" title="Plain English" />
            <div className="rc-plain-english">
                <PlainEnglishGroup title="Report">
                    <li>Uses <strong>{plan.clientName || plan.name || "selected health plan"}</strong> data.</li>
                    {!!coPlans.length && <li>Some metrics also count Co-Plan data: <strong>{coPlans.join(", ")}</strong>. Each metric below says which.</li>}
                    <li>Runs <strong>{report.frequency || "not scheduled"}</strong>.</li>
                    <li>
                        {reportExclusions.length ? "Report-level exclusions:" : "No report-level exclusions configured."}
                        {!!reportExclusions.length && (
                            <ul>
                                {reportExclusions.map((description, index) => (
                                    <li key={`${description}-${index}`}><span className="rc-plain-condition">{description}</span></li>
                                ))}
                            </ul>
                        )}
                    </li>
                </PlainEnglishGroup>
                <PlainEnglishGroup title="Metrics">
                    {report.sections.length
                        ? report.sections.map((section) => <PlainEnglishMetric plan={plan} report={report} section={section} dataModels={dataModels} clientOptions={clientOptions} key={section.id} />)
                        : <li>No metrics configured.</li>}
                </PlainEnglishGroup>
            </div>
        </section>
    );
}

function PlainEnglishMetric({ plan, report, section, dataModels, clientOptions }: { plan: PlanConfig; report: ReportConfig; section: MetricSection; dataModels: Record<string, DataModel>; clientOptions?: ReportConfigurationLookupOption[] }) {
    const model = section.model ? dataModels[section.model] : null;
    const basedOnColumn = section.model
        ? findTimestampColumn(model || undefined, report.modelBasedOn[section.model])
        : null;
    const ruleSet = metricRuleSet(report, section);
    const breakdown = metricBreakdownLabel(report, section, ruleSet);
    const groupBy = groupByLabels(report, section);
    const inclusions = metricInclusionDescriptions(report, section, breakdown);
    const exclusions = metricExclusionDescriptions(report, section, breakdown);
    const coPlans = coPlanLabels(section, clientOptions);

    return (
        <li className="rc-plain-metric">
            <strong>{section.name || "Metric"}</strong>
            <ul>
                {section.periodDate?.applyToReportingPeriod
                    ? <li>Uses <strong>{model?.label || "selected"}</strong> records dated <strong>{periodDateNarrative(report, section) || "by a Period Date that is not finished"}</strong>.</li>
                    : <li>Uses <strong>{model?.label || "selected"}</strong> records based on <strong>{basedOnColumn?.label || "date not selected"}</strong>.</li>}
                {section.periodDate && !section.periodDate.applyToReportingPeriod && <li>Grouped by a Period Date: <strong>{periodDateNarrative(report, section) || "not finished"}</strong>.</li>}
                <li>
                    {coPlans.length
                        ? <>Counts <strong>{planClientLabel(plan)}</strong> plus Co-Plan{coPlans.length === 1 ? "" : "s"} <strong>{coPlans.join(", ")}</strong>.</>
                        : <>Counts <strong>{planClientLabel(plan)}</strong> only - no Co-Plans added.</>}
                </li>
                <li>Period is <strong>{periodLabel(report)}</strong>; display is <strong>{displayAsLabel(section.displayAs)}</strong>.</li>
                <li>
                    Categorization Rule:
                    <ul>
                        {ruleSet ? (
                            <>
                                <li>Name: <strong>{ruleSet.name || "Unnamed rule set"}</strong>.</li>
                                {categorizationGroups(ruleSet, report).map((group) => (
                                    <li key={`${section.id}-${group.category}`}>
                                        Category <strong>{group.category}</strong>
                                        <ul>
                                            {group.rules.map((rule, index) => (
                                                <li key={rule.id}>
                                                    Rule {index + 1}: <span className="rc-plain-condition">{conditionText(rule.conditions, rule.conditionLogic, report)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                ))}
                                {ruleSet.includeElse !== false && <li>
                                    Category <strong>{ruleSet.elseLabel.trim() || "Original value"}</strong>
                                    <ul>
                                        <li>Default fallback when no categorization rule matches.</li>
                                    </ul>
                                </li>}
                            </>
                        ) : <li>No categorization rule selected.</li>}
                    </ul>
                </li>
                <li>{groupBy.length ? <>Group by <strong>{groupBy.join(", ")}</strong>.</> : "No group by configured."}</li>
                <li>
                    {inclusions.length ? "Metric-level inclusions:" : "No metric-level inclusions configured."}
                    {!!inclusions.length && (
                        <ul>
                            {inclusions.map((description, index) => (
                                <li key={`${description}-${index}`}><span className="rc-plain-condition">{description}</span></li>
                            ))}
                        </ul>
                    )}
                </li>
                <li>
                    {exclusions.length ? "Metric-level exclusions:" : "No metric-level exclusions configured."}
                    {!!exclusions.length && (
                        <ul>
                            {exclusions.map((description, index) => (
                                <li key={`${description}-${index}`}><span className="rc-plain-condition">{description}</span></li>
                            ))}
                        </ul>
                    )}
                </li>
            </ul>
        </li>
    );
}

function PlainEnglishGroup({ title, children }: { title: string; children: ReactNode }) {
    return (
        <div className="rc-plain-group">
            <h6>{title}</h6>
            <ul>{children}</ul>
        </div>
    );
}

function PreviewEmpty({ title, text }: { title: string; text: string }) {
    return (
        <div className="rc-rule-summary">
            <section className="rc-summary-card neutral">
                <SectionTitle icon="info" title={title} />
                <p className="rc-summary-muted">{text}</p>
            </section>
        </div>
    );
}

// --- Co-Plan client scope ------------------------------------------------------
// These three mirror sectionClientScope in logic/sqlPreview.ts: the report plan's own
// client is always in scope and is NOT stored on the section (see the MetricSection note
// in types.ts), so it is read from the plan here and the section contributes only the
// extras. An id with no match in the client list is rendered as "Client <id>" rather than
// dropped - a scope naming a client that is no longer active has to stay visible, since
// the query still uses it.
function planClientLabel(plan: PlanConfig) {
    return plan.clientName || plan.name || "this health plan";
}

function coPlanLabels(section: MetricSection, clientOptions?: ReportConfigurationLookupOption[]) {
    const planClientIds = (section.coPlanClientIds || []).filter((id) => Number.isInteger(id) && id > 0);
    return planClientIds.map((id) => clientOptions?.find((option) => option.value === String(id))?.name || `Client ${id}`);
}

function clientScopeLabels(plan: PlanConfig, section: MetricSection, clientOptions?: ReportConfigurationLookupOption[]) {
    return [planClientLabel(plan), ...coPlanLabels(section, clientOptions)];
}

// Every Co-Plan used anywhere in the report, de-duplicated, for the report-level roll-ups.
function reportCoPlanLabels(report: ReportConfig, clientOptions?: ReportConfigurationLookupOption[]) {
    const seen = new Set<string>();
    return report.sections.flatMap((section) => coPlanLabels(section, clientOptions)).filter((label) => {
        if (seen.has(label)) return false;
        seen.add(label);
        return true;
    });
}

function metricHeadline(section?: MetricSection) {
    return section?.name || "No metric configured";
}

function periodLabel(report: ReportConfig) {
    if (isCustomReportingPeriod(report.reportingPeriod.type)) {
        return periodPlainEnglish(report);
    }
    return reportingPeriodName(report.reportingPeriod.type);
}

function displayAsLabel(displayAs: MetricSection["displayAs"]) {
    if (displayAs === "percentage") return "Percentage";
    if (displayAs === "detail") return "Full Row Detail";
    if (displayAs === "otp") return "On-Time Performance";
    return "Count";
}

function detailColumnLabels(section: MetricSection) {
    if (!section.model) return [];
    const columns = detailColumnsForModel(section.model);
    if (!section.selectedColumns.length) return ["All columns"];
    return section.selectedColumns.map((key) => findSelectedDetailColumn(columns, key)?.label || key);
}

function metricRuleSet(report: ReportConfig, section: MetricSection) {
    if (section.breakdownSource !== "ruleSet") return null;
    return report.bucketRuleSets.find((set) => set.id === section.ruleSetId) || null;
}

function metricBreakdownLabel(report: ReportConfig, section: MetricSection, ruleSet = metricRuleSet(report, section)) {
    if (section.breakdownSource === "ruleSet") return ruleSet?.name || "Rule set not selected";
    if (usesCombinedRuleSets(section)) return combinedRuleSetNames(report, section);
    if (!section.model) return "Column not selected";
    return breakdownColumnForModel(section.model, section.breakdownColumn)?.label || section.breakdownColumn || "Column not selected";
}

function groupByLabels(report: ReportConfig, section: MetricSection) {
    if (!section.model || section.displayAs === "detail" || (section.displayAs === "otp" && section.noBreakdown)) return [];
    const labels = [
        usesCombinedRuleSets(section) ? combinedRuleSetNames(report, section) : section.breakdownSource === "ruleSet"
            ? report.bucketRuleSets.find((set) => set.id === section.ruleSetId)?.name || "Category"
            : breakdownColumnForModel(section.model, section.breakdownColumn)?.label || section.breakdownColumn,
        ...section.extraBreakdowns.map((extra) => extraBreakdownLabel(section, extra)),
    ].filter(Boolean) as string[];
    return labels;
}

function ruleResult(rule: CategorizationRule, report: ReportConfig) {
    if (rule.action === "include") return { action: "Include Row", field: "Row", value: "Included Rows" };
    if (rule.action === "exclude") return { action: "Exclude Row", field: "Row", value: "Excluded Rows" };
    const target = targetFields(report).find((field) => field.key === (rule.targetField || "category"));
    return {
        action: "Set Value",
        field: target?.label || "Category",
        // "the value of <column>" for a column-sourced rule.
        value: ruleResultLabel(report, rule) || "(not set)",
    };
}

function conditionValues(condition: RuleCondition) {
    const operator = ruleOpDef(condition.operator);
    if (operator.valueMode === "text") return [condition.text.trim() || "(not set)"];
    if (operator.valueMode === "single") return [condition.values[0] || "(not set)"];
    if (operator.valueMode === "range") return [
        `From ${condition.values[0] || "(not set)"}`,
        `To ${condition.values[1] || "(not set)"}`,
    ];
    if (operator.valueMode === "none") return [];
    return condition.values.length ? condition.values : ["(not set)"];
}

function countLeafConditions(nodes: RuleNode[]): number {
    return nodes.reduce((count, node) => count + (isRuleGroupNode(node) ? countLeafConditions(node.conditions) : 1), 0);
}

function andRuns(nodes: RuleNode[], logic: "and" | "or") {
    const runs: RuleNode[][] = [];
    nodes.forEach((node, index) => {
        const join = index === 0 ? "and" : ruleNodeJoin(node, logic);
        if (!runs.length || join === "or") runs.push([node]);
        else runs[runs.length - 1].push(node);
    });
    return runs;
}

function conditionLevelLabel(nodes: RuleNode[], logic: "and" | "or") {
    const joins = nodes.slice(1).map((node) => ruleNodeJoin(node, logic));
    if (joins.length && joins.some((join) => join !== joins[0])) return "Mixed AND / OR group";
    return (joins[0] || logic) === "or" ? "Any of the following" : "All of the following";
}

function categorizationGroups(ruleSet: RuleSet, report: ReportConfig) {
    const byCategory = new Map<string, CategorizationRule[]>();
    ruleSet.rules.forEach((rule) => {
        const result = ruleResult(rule, report);
        const category = result.value || result.action;
        byCategory.set(category, [...(byCategory.get(category) || []), rule]);
    });
    return Array.from(byCategory.entries()).map(([category, rules]) => ({ category, rules }));
}

function ruleSetDomId(ruleSetId: string) {
    return `rc-summary-ruleset-${safeDomPart(ruleSetId)}`;
}

function safeDomPart(value: string) {
    return String(value || "item").replace(/[^A-Za-z0-9_-]/g, "-");
}

function reportCategories(report: ReportConfig) {
    const categories = new Set<string>();
    report.bucketRuleSets.forEach((ruleSet) => {
        ruleSet.rules.forEach((rule) => {
            // Typed values only: a column-sourced rule has no fixed category to list.
            const value = ruleResultValue(rule);
            if (value) categories.add(value);
        });
        if (ruleSet.includeElse !== false && ruleSet.elseLabel.trim()) categories.add(ruleSet.elseLabel.trim());
    });
    return Array.from(categories);
}

function conditionFromValues(id: string, field: string, operator: string, values: string[]): FlatCondition {
    return { id, field, operator, values: values.length ? values : ["(not set)"] };
}

function exclusionConditions(report: ReportConfig) {
    const conditions: FlatCondition[] = [];
    const globalExclusions = report.globalFilters.exclusions;
    if (globalExclusions.memberIds.length) conditions.push(conditionFromValues("member-exclusions", "Member ID", "Is None Of", globalExclusions.memberIds));
    if (globalExclusions.invoiceNumbers.length) conditions.push(conditionFromValues("invoice-exclusions", "Invoice Number", "Is None Of", globalExclusions.invoiceNumbers));

    return conditions;
}

function metricValueExclusions(section: MetricSection, breakdown: string) {
    if (!section.exclude.length) return [];
    return [conditionFromValues(`metric-${section.id}-exclude`, breakdown || "Metric Value", "Is None Of", section.exclude)];
}

function metricInclusionDescriptions(report: ReportConfig, section: MetricSection, breakdown: string) {
    const includeRows = section.include.length && section.model
        ? [conditionFromValues(`${section.id}-include`, breakdown, "Is Any Of", section.include)]
        : [];
    const filterText = conditionText(metricFilterNodes(section, "filter"), metricFilterLogic(section, "filter"), report);
    return [
        ...includeRows.map(flatConditionText),
        ...(filterText === "all records" ? [] : [filterText]),
    ];
}

function metricExclusionDescriptions(report: ReportConfig, section: MetricSection, breakdown: string) {
    const filterText = conditionText(metricFilterNodes(section, "excludeFilter"), metricFilterLogic(section, "excludeFilter"), report);
    return [
        ...metricValueExclusions(section, breakdown).map(flatConditionText),
        ...(filterText === "all records" ? [] : [filterText]),
    ];
}

function reportExclusionDescriptions(report: ReportConfig) {
    return [
        ...exclusionConditions(report).map(flatConditionText),
        ...tripExclusionGroups(report).map((group) => (
            `${group.title}: ${group.conditions.map(flatConditionText).join(group.logic === "or" ? " OR " : " AND ")}`
        )),
    ];
}

function flatConditionText(condition: FlatCondition) {
    const values = condition.values.join(", ");
    return `${condition.field} ${condition.operator}${values ? ` ${values}` : ""}`;
}

function tripExclusionGroups(report: ReportConfig) {
    const groups: { id: string; title: string; logic: "and" | "or"; conditions: FlatCondition[] }[] = [];
    if (report.models.includes("transportation")) {
        (["exclude"] as FilterGroupKey[]).forEach((groupKey) => {
            const group = report.tripDataFilters[groupKey];
            const conditions: FlatCondition[] = [];
            TRIP_DATA_FIELDS.forEach((field) => {
                const state = group.fields[field.key];
                if (!state) return;
                if (state.values.length) conditions.push(conditionFromValues(`trip-${groupKey}-${field.key}-values`, field.label, "Is Any Of", state.values));
                if (field.hasText && state.freeText.trim()) conditions.push(conditionFromValues(`trip-${groupKey}-${field.key}-exact`, field.label, "Equals", [state.freeText.trim()]));
                if (field.hasText && state.contains.trim()) conditions.push(conditionFromValues(`trip-${groupKey}-${field.key}-contains`, field.label, "Contains Text", [state.contains.trim()]));
            });
            if (conditions.length) groups.push({
                id: `trip-${groupKey}`,
                title: "Trip Data Exclude Filter",
                logic: group.logic,
                conditions,
            });
        });
    }

    return groups;
}

function conditionText(nodes: RuleNode[], logic: "and" | "or", report: ReportConfig): string {
    if (!nodes.length) return "all records";
    const runs = andRuns(nodes, logic);
    return runs.map((run) => {
        const text = run.map((node) => {
            if (isRuleGroupNode(node)) return `(${conditionText(node.conditions, node.conditionLogic, report)})`;
            const condition = node as RuleCondition;
            const columnDisplay = reportColumnDisplayLabel(report, condition.column);
            const values = conditionValues(condition).join(", ");
            return `${columnDisplay || condition.column || "field"} ${ruleOpDef(condition.operator).label}${values ? ` ${values}` : ""}`;
        }).join(" and ");
        return runs.length > 1 && run.length > 1 ? `(${text})` : text;
    }).join(" or ");
}
